import { Eye, MessageSquareText } from 'lucide-react';

import type { ResearchSummary } from '@/api/types';
import { formatDateTime } from '@/ui/format';
import { Markdown } from '@/ui/Markdown';

import { VerdictPill } from '../ideaUi';
import { ScoreValue } from '@/ui/ScoreValue';
import { ResearchStatusPill } from '@/ui/ResearchStatusPill';
import { durationFallback, historyRowClass, modelFallback, promptNotYet } from './historyCells';
import { PromptVersionLabel } from './PromptVersionLabel';
import { runDuration } from './scoring';

export interface HistoryRowProps {
  run: ResearchSummary;
  striped: boolean;
  isCurrent: boolean;
  isViewing: boolean;
  shown: boolean;
  utc: boolean;
  onToggle: (id: string) => void;
  onView: (id: string | null) => void;
}

function ModelCell({ run }: { run: ResearchSummary }) {
  if (run.model_name) return <span title={run.model_key ?? undefined}>{run.model_name}</span>;
  const fallback = modelFallback(run);
  return (
    <span className={fallback.className} title={fallback.title}>
      {fallback.text}
    </span>
  );
}

function RowActions({ run, isCurrent, isViewing, shown, utc, onToggle, onView }: Omit<HistoryRowProps, 'striped'>) {
  const requested = formatDateTime(run.created_at, utc);
  return (
    <div className="row-actions">
      {run.refinement && (
        <button
          type="button"
          className="btn btn-secondary btn-icon refinement-toggle"
          data-tooltip={shown ? 'Hide the refinement' : 'Show the refinement'}
          aria-label={`${shown ? 'Hide' : 'Show'} the refinement of the run requested ${requested}`}
          aria-expanded={shown}
          aria-controls={`refinement-${run.id}`}
          onClick={() => onToggle(run.id)}
        >
          <MessageSquareText aria-hidden="true" />
        </button>
      )}
      {run.status === 'succeeded' && !isViewing && (
        <button
          type="button"
          className="btn btn-secondary btn-icon"
          data-tooltip="View this run"
          aria-label={`View the run requested ${requested}`}
          onClick={() => onView(isCurrent ? null : run.id)}
        >
          <Eye aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

function RefinementRow({ run, refinement, isViewing }: { run: ResearchSummary; refinement: string; isViewing: boolean }) {
  return (
    <tr id={`refinement-${run.id}`} className={`history-refinement${isViewing ? ' is-expanded' : ''}`}>
      <td colSpan={9}>
        <span className="refinement-meta">Refinement</span>
        <div className="history-refinement-text">
          <Markdown>{refinement}</Markdown>
        </div>
      </td>
    </tr>
  );
}

function StatusCell({ run }: { run: ResearchSummary }) {
  return (
    <td>
      <ResearchStatusPill status={run.status} />
      {/* A run that succeeded on a retry may still carry an earlier attempt's error. */}
      {run.status === 'failed' && run.error && <span className="sub run-error">{run.error}</span>}
    </td>
  );
}

/** One run in the history table, and its refinement under it when open. */
export function HistoryRow(props: HistoryRowProps) {
  const { run, striped, isCurrent, isViewing, shown, utc } = props;
  const refinement = shown ? run.refinement : null;
  const refinementShown = Boolean(refinement);
  return (
    <>
      <tr className={historyRowClass({ striped, viewing: isViewing, refinementShown })}>
        <td className="nowrap">
          {formatDateTime(run.created_at, utc)}
          {isViewing && !isCurrent && <span className="badge badge-neutral history-current">Viewing</span>}
        </td>
        <StatusCell run={run} />
        <td className="num nowrap">{runDuration(run) ?? <span className="hint">{durationFallback(run)}</span>}</td>
        <td className="num"><ScoreValue value={run.total} /></td>
        <td><VerdictPill verdict={run.verdict} /></td>
        <td>
          <ModelCell run={run} />
        </td>
        <td className="hint">{promptNotYet(run) ? 'Not yet' : <PromptVersionLabel run={run} />}</td>
        <td>{run.requested_by?.display_name ?? 'Not recorded'}</td>
        <td>
          <RowActions {...props} />
        </td>
      </tr>
      {refinement && <RefinementRow run={run} refinement={refinement} isViewing={isViewing} />}
    </>
  );
}
